// crewbit-admin/src/components/money/TaxBoxCell.tsx
//
// Single cell inside TaxBoxStrip. Label + amount + plain-English description.
// Tabular nums on amount per Hard Rule #7. Currency via formatMoney.
//
// Tone:
//   - 'default' — body weight, neutral surface
//   - 'emphasis' — Box 109 net (the take-home number); bolded + accent-tinted
//                  background to anchor the strip.

import { formatMoney } from '../../lib/money';
import { palette, admin, radii, spacing } from '../../styles/tokens';

export interface TaxBoxCellProps {
  label: string;
  amount: number | null;
  description?: string;
  /** When provided, replaces the formatted dollar amount entirely (e.g. percentage strings). */
  valueOverride?: string;
  tone?: 'default' | 'emphasis';
}

export function TaxBoxCell({ label, amount, description, valueOverride, tone = 'default' }: TaxBoxCellProps) {
  const isEmphasis = tone === 'emphasis';
  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        background: isEmphasis ? palette.accentWash : palette.white,
        border: `1px solid ${isEmphasis ? palette.accent : palette.g150}`,
        borderRadius: radii.md,
        padding: `${spacing.md}px ${spacing.md}px`,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xs,
      }}
    >
      <div
        style={{
          fontSize: 11.5,
          fontWeight: 600,
          color: isEmphasis ? palette.accent : palette.g500,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: isEmphasis ? 22 : 18,
          fontWeight: isEmphasis ? 700 : 600,
          color: isEmphasis ? palette.accent : admin.header,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.01em',
          lineHeight: 1.15,
        }}
      >
        {valueOverride ?? formatMoney(amount)}
      </div>
      {description && (
        <div style={{ fontSize: 11.5, color: palette.g500, lineHeight: 1.35 }}>
          {description}
        </div>
      )}
    </div>
  );
}
